/**
 * Logo Component
 * Componente de logo do Vover com suporte para texto "admin" abaixo
 */

import React, { useState, useEffect } from 'react';
import { Box, BoxProps, Typography, useTheme, useMediaQuery } from '@mui/material';

interface LogoProps extends Omit<BoxProps, 'component'> {
  /**
   * Altura da logo em pixels
   * @default 24
   */
  height?: number;
  /**
   * Variante da logo
   * - 'logo': Logo completa com texto
   * - 'icon': Apenas o ícone
   * - 'auto': Automático baseado no tamanho da tela
   * @default 'auto'
   */
  variant?: 'logo' | 'icon' | 'auto';
  /**
   * Cor da logo
   * - 'white': Logo branca (para fundos escuros)
   * - 'gray': Logo cinza (para fundos claros)
   * - 'default': Logo original
   * @default 'default'
   */
  color?: 'white' | 'gray' | 'default';
  /**
   * Se true, mostra texto "admin" abaixo da logo
   * @default false
   */
  showAdminText?: boolean;
  /**
   * Se true, a logo será clicável
   * @default false
   */
  clickable?: boolean;
  /**
   * Callback quando a logo for clicada
   */
  onClick?: () => void;
}

const Logo: React.FC<LogoProps> = ({
  height = 24,
  variant = 'auto',
  color = 'default',
  showAdminText = false,
  clickable = false,
  onClick,
  sx,
  ...boxProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [imageError, setImageError] = useState(false);

  // Determinar qual variante usar
  let imageVariant: 'logo' | 'icon';
  if (variant === 'auto') {
    imageVariant = isMobile ? 'icon' : 'logo';
  } else {
    imageVariant = variant;
  }

  // Determinar arquivo baseado na variante e cor
  let imageSrc: string;
  let altText: string;

  if (imageVariant === 'icon') {
    imageSrc = '/images/logo_v.png';
    altText = 'Vover Icon';
  } else {
    switch (color) {
      case 'white':
        imageSrc = '/images/logo_vover.png';
        altText = 'Vover Logo (White)';
        break;
      case 'gray':
        imageSrc = '/images/logo_vover.png';
        altText = 'Vover Logo (Gray)';
        break;
      case 'default':
      default:
        imageSrc = '/images/logo_vover.png';
        altText = 'Vover Logo';
        break;
    }
  }

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  const handleImageError = () => {
    if (!imageError && imageVariant === 'logo') {
      setImageError(true);
    }
  };

  useEffect(() => {
    setImageError(false);
  }, [imageSrc, imageVariant]);

  const finalSrc = imageError && imageVariant === 'logo' ? '/images/logo_v.png' : imageSrc;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: showAdminText ? 'row' : 'column',
        alignItems: showAdminText ? 'flex-end' : 'flex-start',
        cursor: clickable ? 'pointer' : 'default',
        gap: showAdminText ? 0.5 : 0,
        ...sx
      }}
      onClick={clickable ? handleClick : undefined}
      {...boxProps}
    >
      <Box
        component="img"
        src={finalSrc}
        alt={altText}
        onError={handleImageError}
        sx={{
          height: `${height}px`,
          width: 'auto',
          display: 'block',
          // Em fundos escuros (color="white"), aplicar filtro para logo ficar visível
          ...(color === 'white' && {
            filter: 'brightness(0) invert(1)'
          })
        }}
      />
      {showAdminText && (
        <Typography
          component="span"
          sx={{
            fontSize: '0.55rem',
            fontWeight: 400,
            letterSpacing: '0.5px',
            color: 'inherit',
            lineHeight: 1,
            opacity: 0.85
          }}
        >
          admin
        </Typography>
      )}
    </Box>
  );
};

export default Logo;
